import type { AiElementsTokens } from '@modernloop/ai-elements';
import type { Theme } from 'src/themeMui5/type';

/**
 * The opaque surface shadcn expects behind hovered and muted controls.
 *
 * `action.hover` cannot stand in for it: MUI ships that as a low-alpha overlay meant to be
 * composited onto a surface, but shadcn assigns it as a background outright, so a hovered control
 * would turn translucent and show whatever sits behind it.
 */
const opaqueSurface = (theme: Theme) =>
  theme.palette.background.contrast ??
  (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]);

/** Converts the host MUI theme into the CSS token boundary consumed by AI Elements. */
export const muiThemeToAiTokens = (theme: Theme): AiElementsTokens => ({
  background: theme.palette.background.default,
  foreground: theme.palette.text.primary,
  card: theme.palette.background.paper,
  cardForeground: theme.palette.text.primary,
  popover: theme.palette.background.paper,
  popoverForeground: theme.palette.text.primary,
  primary: theme.palette.primary.main,
  primaryForeground: theme.palette.primary.contrastText,
  secondary: theme.palette.secondary.dark,
  secondaryForeground: theme.palette.secondary.contrastText,
  muted: opaqueSurface(theme),
  mutedForeground: theme.palette.text.secondary,
  accent: opaqueSurface(theme),
  accentForeground: theme.palette.text.primary,
  destructive: theme.palette.error.main,
  destructiveForeground: theme.palette.error.contrastText,
  border: theme.palette.divider,
  input: theme.palette.divider,
  ring: theme.palette.primary.main,
  radius: `${theme['shape'].borderRadius}px`,
  fontSans: theme.typography.fontFamily ?? 'sans-serif',
});
